import { useState, useMemo } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../components/ui/collapsible';
import {
  Receipt, ChevronDown, FileText, Filter, CalendarDays, Zap, RefreshCw
} from 'lucide-react';
import { format } from 'date-fns';

const TABS = [
  { key: 'all', label: 'All' },
  { key: 'payg', label: 'Pay As You Go' },
  { key: 'subscription', label: 'Subscriptions' },
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All statuses' },
  { value: 'paid', label: 'Paid' },
  { value: 'completed', label: 'Completed' },
  { value: 'initiated', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'refunded', label: 'Refunded' },
];

const PLAN_OPTIONS = [
  { value: 'all', label: 'All plans' },
  { value: 'payg', label: 'Pay As You Go' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
];

function getMonthKey(dateStr) {
  if (!dateStr) return 'unknown';
  try {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  } catch {
    return 'unknown';
  }
}

function formatMonthLabel(key) {
  if (key === 'unknown') return 'Unknown';
  const [y, m] = key.split('-');
  const d = new Date(parseInt(y), parseInt(m) - 1, 1);
  return format(d, 'MMMM yyyy');
}

function statusBadge(status) {
  const isPaid = status === 'paid' || status === 'completed';
  const cls = isPaid ? 'bg-emerald-100 text-emerald-800'
    : status === 'initiated' ? 'bg-amber-100 text-amber-800'
    : status === 'failed' ? 'bg-red-100 text-red-800'
    : status === 'refunded' ? 'bg-blue-100 text-blue-800'
    : 'bg-slate-100 text-slate-600';
  const label = isPaid ? 'Paid' : status === 'initiated' ? 'Pending' : status;
  return <Badge className={`text-xs capitalize ${cls}`}>{label}</Badge>;
}

function formatDate(iso) {
  if (!iso) return '-';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function PaymentHistory({ transactions }) {
  const [activeTab, setActiveTab] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [expandedMonths, setExpandedMonths] = useState(new Set());
  const [initialized, setInitialized] = useState(false);

  // Apply tab + filters
  const filtered = useMemo(() => {
    return transactions.filter(t => {
      if (activeTab !== 'all' && t.category !== activeTab) return false;
      if (filterPlan !== 'all') {
        const pt = t.planType === 'yearly' ? 'annual' : t.planType;
        if (pt !== filterPlan) return false;
      }
      if (filterStatus !== 'all') {
        const s = t.paymentStatus;
        if (filterStatus === 'paid' && s !== 'paid' && s !== 'completed') return false;
        if (filterStatus !== 'paid' && s !== filterStatus) return false;
      }
      return true;
    });
  }, [transactions, activeTab, filterPlan, filterStatus]);

  // Group by month
  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of filtered) {
      const key = getMonthKey(t.createdAt);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    // Sort months descending
    const sorted = [...map.entries()].sort((a, b) => b[0].localeCompare(a[0]));
    return sorted;
  }, [filtered]);

  // Auto-expand first month on initial load
  if (!initialized && grouped.length > 0) {
    setExpandedMonths(new Set([grouped[0][0]]));
    setInitialized(true);
  }

  const toggleMonth = (key) => {
    setExpandedMonths(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const clearFilters = () => {
    setFilterPlan('all');
    setFilterStatus('all');
  };
  const hasActiveFilters = filterPlan !== 'all' || filterStatus !== 'all';

  return (
    <Card className="border-0 shadow-sm" data-testid="payment-history-section">
      <CardContent className="p-6">
        <h2 className="text-lg font-bold text-slate-900 mb-5 flex items-center gap-2">
          <Receipt className="h-5 w-5 text-slate-400" />
          Payment History
        </h2>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-4 bg-slate-100 rounded-full p-1 w-fit max-w-full overflow-x-auto" data-testid="payment-history-tabs">
          {TABS.map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all min-h-[44px] whitespace-nowrap ${
                activeTab === tab.key
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-500 hover:text-slate-700'
              }`}
              data-testid={`tab-${tab.key}`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Filter toggle + controls */}
        <div className="mb-4">
          <button
            onClick={() => setShowFilters(p => !p)}
            className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 transition-colors min-h-[44px] px-1"
            data-testid="toggle-filters-btn"
          >
            <Filter className="h-4 w-4" />
            Filters
            {hasActiveFilters && (
              <Badge className="bg-[#DA291C] text-white text-[10px] ml-1 px-1.5 py-0">
                {(filterPlan !== 'all' ? 1 : 0) + (filterStatus !== 'all' ? 1 : 0)}
              </Badge>
            )}
          </button>

          {showFilters && (
            <div className="mt-3 flex flex-wrap items-end gap-3 p-4 bg-slate-50 rounded-xl border border-slate-200 animate-in slide-in-from-top-2 duration-200" data-testid="filters-panel">
              <div className="space-y-1 flex-1 min-w-[140px]">
                <label className="text-xs font-medium text-slate-500">Plan</label>
                <Select value={filterPlan} onValueChange={setFilterPlan}>
                  <SelectTrigger className="h-11 w-full sm:w-40 rounded-lg text-sm bg-white" data-testid="filter-plan">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PLAN_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 flex-1 min-w-[140px]">
                <label className="text-xs font-medium text-slate-500">Status</label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="h-11 w-full sm:w-40 rounded-lg text-sm bg-white" data-testid="filter-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map(o => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" className="text-xs text-slate-500 h-11" onClick={clearFilters} data-testid="clear-filters-btn">
                  Clear filters
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Content: Grouped or empty */}
        {transactions.length === 0 ? (
          <div className="p-10 text-center" data-testid="no-payments-empty">
            <Receipt className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No payments yet.</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center" data-testid="no-matching-empty">
            <Filter className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">No matching payments.</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="grouped-payment-list">
            {grouped.map(([monthKey, txns]) => {
              const isOpen = expandedMonths.has(monthKey);
              const monthTotal = txns.reduce((sum, t) => sum + (t.amount || 0), 0);

              return (
                <Collapsible key={monthKey} open={isOpen} onOpenChange={() => toggleMonth(monthKey)}>
                  <CollapsibleTrigger asChild>
                    <button
                      className={`w-full flex items-center justify-between p-4 rounded-xl transition-all ${
                        isOpen ? 'bg-slate-100' : 'bg-slate-50 hover:bg-slate-100'
                      }`}
                      data-testid={`month-group-${monthKey}`}
                    >
                      <div className="flex items-center gap-3">
                        <CalendarDays className="h-4 w-4 text-slate-400" />
                        <span className="font-semibold text-slate-900 text-sm">{formatMonthLabel(monthKey)}</span>
                        <Badge className="bg-slate-200 text-slate-600 text-xs">{txns.length}</Badge>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-sm font-semibold text-slate-700">
                          &pound;{monthTotal.toFixed(2)}
                        </span>
                        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
                      </div>
                    </button>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="mt-1 overflow-x-auto">
                      <table className="w-full text-sm" data-testid={`txn-table-${monthKey}`}>
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-2.5 px-3 text-[11px] font-semibold text-slate-400 uppercase">Date</th>
                            <th className="text-left py-2.5 px-3 text-[11px] font-semibold text-slate-400 uppercase">Plan</th>
                            <th className="text-left py-2.5 px-3 text-[11px] font-semibold text-slate-400 uppercase">Description</th>
                            <th className="text-right py-2.5 px-3 text-[11px] font-semibold text-slate-400 uppercase">Amount</th>
                            <th className="text-right py-2.5 px-3 text-[11px] font-semibold text-slate-400 uppercase">Status</th>
                            <th className="text-right py-2.5 px-3 text-[11px] font-semibold text-slate-400 uppercase"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {txns.map(t => (
                            <tr key={t.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`txn-row-${t.id}`}>
                              <td className="py-3 px-3 text-slate-600 whitespace-nowrap">{formatDate(t.createdAt)}</td>
                              <td className="py-3 px-3">
                                <div className="flex items-center gap-1.5">
                                  {t.category === 'payg'
                                    ? <Zap className="h-3.5 w-3.5 text-blue-500" />
                                    : <RefreshCw className="h-3.5 w-3.5 text-indigo-500" />}
                                  <span className="text-slate-700 font-medium">{t.planLabel}</span>
                                </div>
                              </td>
                              <td className="py-3 px-3 text-slate-900">{t.description}</td>
                              <td className="py-3 px-3 text-right font-semibold text-slate-900 whitespace-nowrap">
                                {t.amount > 0 ? `£${t.amount.toFixed(2)}` : '—'}
                              </td>
                              <td className="py-3 px-3 text-right">
                                {statusBadge(t.paymentStatus)}
                              </td>
                              <td className="py-3 px-3 text-right">
                                {(t.paymentStatus === 'paid' || t.paymentStatus === 'completed') && (
                                  <button
                                    className="text-slate-400 hover:text-slate-700 transition-colors inline-flex items-center justify-center h-11 w-11 rounded-lg hover:bg-slate-100"
                                    title="View receipt"
                                    data-testid={`receipt-btn-${t.id}`}
                                  >
                                    <FileText className="h-4 w-4" />
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
